import React, { useState, useEffect } from 'react'
import { Sun, Cloud, CloudRain, CloudSnow, Wind, MapPin, Eye, EyeOff, Settings, Calendar } from 'lucide-react'

interface WeatherData {
  temperature: number
  feelsLike: number
  condition: string
  description: string
  humidity: number
  windSpeed: number
  pressure: number
  visibility: number
  location: string
  zipCode: string
  icon: string
  provider: string
}

const WeatherWidget: React.FC = () => {
  const [weather, setWeather] = useState<WeatherData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showForecast, setShowForecast] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [zipCode, setZipCode] = useState('75494') // Default to Winnsboro, TX
  const [apiKey, setApiKey] = useState('')
  const [weatherService, setWeatherService] = useState('openmeteo') // 'weatherapi' or 'openmeteo'
  const [forecast, setForecast] = useState<any[]>([])


  // Load saved zip code and API key from localStorage
  useEffect(() => {
    const savedZipCode = localStorage.getItem('weather-zip-code')
    const savedApiKey = localStorage.getItem('weather-api-key')
    const savedService = localStorage.getItem('weather-service')
    
    if (savedZipCode) setZipCode(savedZipCode)
    if (savedApiKey) setApiKey(savedApiKey)
    if (savedService) setWeatherService(savedService)
    
    // Initial weather fetch after loading settings
    if (savedZipCode) {
      setTimeout(() => {
        if (savedService === 'openmeteo') {
          fetchWeatherData(savedZipCode)
        } else if (savedService === 'weatherapi' && savedApiKey) {
          fetchWeatherData(savedZipCode)
        } else {
          // Default to OpenMeteo if no service saved
          fetchWeatherData(savedZipCode)
        }
      }, 100)
    }
  }, [])

  const fetchWeatherData = async (zip: string) => {
    setLoading(true)
    setError(null)

    try {
      let weatherData: WeatherData | null = null

      if (weatherService === 'weatherapi') {
        if (!apiKey) {
          throw new Error('Please enter your WeatherAPI.com API key in settings')
        }
        weatherData = await fetchWeatherAPI(zip)
      } else if (weatherService === 'openmeteo') {
        weatherData = await fetchOpenMeteo(zip)
      } else {
        // Fallback to OpenMeteo if no service selected
        weatherData = await fetchOpenMeteo(zip)
      }

      if (weatherData) {
        setWeather(weatherData)
        localStorage.setItem('weather-zip-code', zip)
        localStorage.setItem('weather-service', weatherService)
        

      }
    } catch (err) {
      console.error('Weather API error:', err)
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Failed to load weather data. Please check your settings.')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchWeatherAPI = async (zip: string): Promise<WeatherData> => {
    const url = `https://api.weatherapi.com/v1/current.json?key=${apiKey}&q=${zip}&aqi=no`
    console.log('Fetching from WeatherAPI.com:', url)
    
    const response = await fetch(url)
    
    if (!response.ok) {
      const errorText = await response.text()
      if (response.status === 401) {
        throw new Error('Invalid WeatherAPI.com API key. Please check your key.')
      } else if (response.status === 400) {
        throw new Error('Location not found. Please check your ZIP code.')
      } else {
        throw new Error(`WeatherAPI.com error: ${response.status} - ${errorText}`)
      }
    }

    const data = await response.json()
    console.log('WeatherAPI.com data:', data)

    return {
      temperature: Math.round(data.current.temp_f),
      feelsLike: Math.round(data.current.feelslike_f),
      condition: data.current.condition.text,
      description: data.current.condition.text,
      humidity: data.current.humidity,
      windSpeed: Math.round(data.current.wind_mph),
      pressure: Math.round(data.current.pressure_mb),
      visibility: Math.round(data.current.vis_miles),
      location: data.location.name,
      zipCode: zip,
      icon: data.current.condition.icon,
      provider: 'WeatherAPI.com'
    }
  }

  const fetchOpenMeteo = async (zip: string): Promise<WeatherData> => {
    // First get coordinates from ZIP code using a geocoding service
    const geoResponse = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${zip}&country=US&count=1`)
    const geoData = await geoResponse.json()
    
    if (!geoData.results || geoData.results.length === 0) {
      throw new Error('Location not found. Please check your ZIP code.')
    }

    const { latitude, longitude, name } = geoData.results[0]
    
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,apparent_temperature,pressure_msl,wind_speed_10m,visibility&daily=temperature_2m_max,temperature_2m_min,weathercode&temperature_unit=fahrenheit&wind_speed_unit=mph&visibility_unit=miles&timezone=auto`
    console.log('Fetching from OpenMeteo:', url)
    
    const response = await fetch(url)
    
    if (!response.ok) {
      throw new Error('Failed to fetch weather data from OpenMeteo')
    }

    const data = await response.json()
    console.log('OpenMeteo data:', data)

    const current = data.current
    
    // Process forecast data
    const forecastData = []
    if (data.daily) {
      for (let i = 0; i < Math.min(3, data.daily.time.length); i++) {
        forecastData.push({
          date: data.daily.time[i],
          temperature: (data.daily.temperature_2m_max[i] + data.daily.temperature_2m_min[i]) / 2,
          condition: getWeatherConditionFromCode(data.daily.weathercode[i])
        })
      }
    }
    setForecast(forecastData)

    return {
      temperature: Math.round(current.temperature_2m),
      feelsLike: Math.round(current.apparent_temperature),
      condition: getWeatherCondition(current.temperature_2m, current.relative_humidity_2m),
      description: getWeatherDescription(current.temperature_2m, current.relative_humidity_2m),
      humidity: current.relative_humidity_2m,
      windSpeed: Math.round(current.wind_speed_10m),
      pressure: Math.round(current.pressure_msl),
      visibility: Math.round(current.visibility),
      location: name,
      zipCode: zip,
      icon: getWeatherIconCode(current.temperature_2m, current.relative_humidity_2m),
      provider: 'OpenMeteo (Free)'
    }
  }

  const getWeatherCondition = (temp: number, humidity: number): string => {
    if (temp < 32) return 'Snow'
    if (temp < 50) return 'Cold'
    if (temp > 80) return 'Hot'
    if (humidity > 80) return 'Humid'
    return 'Clear'
  }

  const getWeatherDescription = (temp: number, humidity: number): string => {
    if (temp < 32) return 'Cold and snowy'
    if (temp < 50) return 'Cool weather'
    if (temp > 80) return 'Hot weather'
    if (humidity > 80) return 'Humid conditions'
    return 'Clear skies'
  }

  const getWeatherIconCode = (temp: number, humidity: number): string => {
    if (temp < 32) return '13d'
    if (temp < 50) return '03d'
    if (temp > 80) return '01d'
    if (humidity > 80) return '04d'
    return '01d'
  }

  const getWeatherConditionFromCode = (code: number): string => {
    // WMO Weather interpretation codes
    switch (code) {
      case 0: return 'Clear'
      case 1: case 2: case 3: return 'Cloudy'
      case 45: case 48: return 'Foggy'
      case 51: case 53: case 55: return 'Drizzle'
      case 56: case 57: return 'Freezing Drizzle'
      case 61: case 63: case 65: return 'Rain'
      case 66: case 67: return 'Freezing Rain'
      case 71: case 73: case 75: return 'Snow'
      case 77: return 'Snow Grains'
      case 80: case 81: case 82: return 'Rain Showers'
      case 85: case 86: return 'Snow Showers'
      case 95: return 'Thunderstorm'
      case 96: case 99: return 'Thunderstorm with Hail'
      default: return 'Clear'
    }
  }



  useEffect(() => {
    if (zipCode) {
      // For OpenMeteo, we don't need an API key
      if (weatherService === 'openmeteo') {
        fetchWeatherData(zipCode)
      }
      // For WeatherAPI, we need both API key and zip code
      else if (weatherService === 'weatherapi' && apiKey) {
        fetchWeatherData(zipCode)
      }
    }
  }, [apiKey, zipCode, weatherService])

  const getWeatherIcon = (condition: string, iconCodeOrTemp: string | number) => {
    const conditionLower = condition.toLowerCase()
    const iconClass = typeof iconCodeOrTemp === 'number' ? "w-6 h-6" : "w-8 h-8"
    
    // If we have an iconCode (current weather), use that logic
    if (typeof iconCodeOrTemp === 'string') {
      const iconCode = iconCodeOrTemp
      if (iconCode.includes('01')) {
        return <Sun className={`${iconClass} text-yellow-500`} />
      } else if (iconCode.includes('02') || iconCode.includes('03') || iconCode.includes('04')) {
        return <Cloud className={`${iconClass} text-gray-500`} />
      } else if (iconCode.includes('09') || iconCode.includes('10')) {
        return <CloudRain className={`${iconClass} text-blue-500`} />
      } else if (iconCode.includes('13')) {
        return <CloudSnow className={`${iconClass} text-blue-300`} />
      } else if (iconCode.includes('11')) {
        return <CloudRain className={`${iconClass} text-yellow-500`} />
      } else {
        return <Wind className={`${iconClass} text-gray-400`} />
      }
    }
    
    // If we have temperature (forecast), use condition-based logic
    const temperature = iconCodeOrTemp as number
    switch (conditionLower) {
      case 'clear':
        return <Sun className={`${iconClass} text-yellow-500`} />
      case 'cloudy':
        return <Cloud className={`${iconClass} text-gray-500`} />
      case 'foggy':
        return <Cloud className={`${iconClass} text-gray-400`} />
      case 'drizzle':
      case 'rain':
        return <CloudRain className={`${iconClass} text-blue-500`} />
      case 'freezing drizzle':
      case 'freezing rain':
        return <CloudRain className={`${iconClass} text-blue-300`} />
      case 'snow':
      case 'snow grains':
      case 'snow showers':
        return <CloudSnow className={`${iconClass} text-blue-200`} />
      case 'rain showers':
        return <CloudRain className={`${iconClass} text-blue-600`} />
      case 'thunderstorm':
      case 'thunderstorm with hail':
        return <CloudRain className={`${iconClass} text-purple-600`} />
      default:
        // Fallback based on temperature
        if (temperature < 32) return <CloudSnow className={`${iconClass} text-blue-200`} />
        if (temperature < 50) return <Cloud className={`${iconClass} text-gray-500`} />
        if (temperature > 80) return <Sun className={`${iconClass} text-yellow-500`} />
        return <Sun className={`${iconClass} text-yellow-400`} />
    }
  }

  const handleSaveSettings = () => {
    localStorage.setItem('weather-api-key', apiKey)
    localStorage.setItem('weather-zip-code', zipCode)
    localStorage.setItem('weather-service', weatherService)
    setShowSettings(false)
    if (zipCode) {
      // For OpenMeteo, we don't need an API key
      if (weatherService === 'openmeteo') {
        fetchWeatherData(zipCode)
      }
      // For WeatherAPI, we need both API key and zip code
      else if (weatherService === 'weatherapi' && apiKey) {
        fetchWeatherData(zipCode)
      }
    }
  }

  const testApiKey = async () => {
    if (!apiKey) {
      alert('Please enter an API key first')
      return
    }

    try {
      const response = await fetch(
        `https://api.weatherapi.com/v1/current.json?key=${apiKey}&q=10001&aqi=no`
      )
      
      if (response.ok) {
        alert('✅ WeatherAPI.com API key is working! You can now save your settings.')
      } else {
        const errorText = await response.text()
        alert(`❌ API key test failed: ${response.status} - ${errorText}`)
      }
    } catch (err) {
      alert(`❌ API key test failed: ${err}`)
    }
  }





  if (showSettings) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Weather Settings</h3>
          <button
            onClick={() => setShowSettings(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <EyeOff className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Weather Service
            </label>
            <select
              value={weatherService}
              onChange={(e) => setWeatherService(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
            >
              <option value="openmeteo">OpenMeteo (Free - No API Key Required)</option>
              <option value="weatherapi">WeatherAPI.com (Free - API Key Required)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              OpenMeteo is completely free and requires no setup. WeatherAPI.com offers more features with a free API key.
            </p>
          </div>

          {weatherService === 'weatherapi' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                WeatherAPI.com API Key
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your API key"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Get a free API key at <a href="https://www.weatherapi.com/signup.aspx" target="_blank" rel="noopener noreferrer" className="text-grace-blue-600 hover:underline">weatherapi.com</a>
              </p>
              <button
                onClick={testApiKey}
                className="mt-2 text-sm text-grace-blue-600 hover:text-grace-blue-700 underline"
              >
                Test API Key
              </button>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ZIP Code
            </label>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              placeholder="Enter ZIP code"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
            />
          </div>
          
          <button
            onClick={handleSaveSettings}
            className="w-full btn-primary"
          >
            Save Settings
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="card p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Weather</h3>
          <button
            onClick={() => setShowSettings(true)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
        <p className="text-red-500 text-sm mb-4">{error}</p>
        <button
          onClick={() => setShowSettings(true)}
          className="btn-primary text-sm"
        >
          Configure Weather
        </button>
      </div>
    )
  }

  if (!weather) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Weather</h3>
          <button
            onClick={() => setShowSettings(true)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-500 mb-4">Configure weather settings to get started</p>
        <button
          onClick={() => setShowSettings(true)}
          className="btn-primary text-sm"
        >
          Setup Weather
        </button>
      </div>
    )
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Weather in {weather.location}</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowForecast(!showForecast)}
            className="flex items-center space-x-1 text-gray-400 hover:text-gray-600"
            title={showForecast ? "Hide Forecast" : "Show Forecast"}
          >
            {showForecast ? <EyeOff className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
            <span className="text-sm">Forecast</span>
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="text-gray-400 hover:text-gray-600"
            title="Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {showForecast && (
        <div className="mb-4 p-2 bg-gray-50 rounded-lg">
          <div className="text-xs text-gray-600 mb-2">3-Day Forecast</div>
          
          <div className="grid grid-cols-3 gap-2">
            {forecast.slice(0, 3).map((day, index) => (
              <div key={index} className="text-center p-2 bg-white rounded border">
                <div className="text-xs font-medium text-gray-600">
                  {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className="flex justify-center my-1">
                  {getWeatherIcon(day.condition, day.temperature)}
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {Math.round(day.temperature)}°F
                </div>
                <div className="text-xs text-gray-500">
                  {day.condition}
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500 mt-2">
            Forecast data from {weatherService === 'openmeteo' ? 'OpenMeteo' : 'WeatherAPI.com'}
          </p>
        </div>
      )}
      
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          {getWeatherIcon(weather.condition, weather.icon)}
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {weather.temperature}°F
            </div>
            <div className="text-sm text-gray-600">
              Feels like {weather.feelsLike}°F
            </div>
            <div className="text-sm text-gray-600 capitalize">
              {weather.description}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex justify-between">
          <span>Humidity:</span>
          <span>{weather.humidity}%</span>
        </div>
        <div className="flex justify-between">
          <span>Wind:</span>
          <span>{weather.windSpeed} mph</span>
        </div>
        <div className="flex justify-between">
          <span>Pressure:</span>
          <span>{weather.pressure} hPa</span>
        </div>
        <div className="flex justify-between">
          <span>Visibility:</span>
          <span>{weather.visibility} mi</span>
        </div>
        <div className="flex justify-between">
          <span>Location:</span>
          <span className="text-right flex items-center">
            <MapPin className="w-3 h-3 mr-1" />
            {weather.location} ({weather.zipCode})
          </span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Data from {weather.provider} • Last updated: {new Date().toLocaleTimeString()}
        </p>
      </div>
    </div>
  )
}

export default WeatherWidget 